// Runs inside the isolated iframe. Keep this function self-contained.
export function installWidgetTheme() {
  let theme = { mode: 'light', tokens: {} };
  const literal = /#[\da-f]{3,8}\b|rgba?\([^)]*\)|\b(?:white|black)\b/gi;
  function tokenFor(color, property) {
    let rgb;
    if (color[0] === '#') {
      let hex = color.slice(1);
      if (hex.length === 3 || hex.length === 4) hex = [...hex].map(c => c + c).join('');
      if (hex.length !== 6 && hex.length !== 8) return null;
      rgb = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16));
    } else if (/^rgba?\(/i.test(color)) {
      rgb = color.match(/[\d.]+/g)?.slice(0, 3).map(Number);
    } else rgb = color.toLowerCase() === 'white' ? [255, 255, 255] : [0, 0, 0];
    if (!rgb || rgb.length !== 3) return null;
    const [r, g, b] = rgb;
    const accent = Math.abs(r - 64) < 4 && Math.abs(g - 224) < 4 && Math.abs(b - 208) < 4;
    // Neutral/slate UI colors; leave saturated series and status colors intact.
    const neutral = Math.max(...rgb) - Math.min(...rgb) <= 65;
    if (!neutral && !accent) return null;
    if (/border|outline|shadow|stroke|grid/i.test(property)) return '--color-border';
    if (/background|surface|bg|fillRect/i.test(property)) return accent ? '--color-primary-subtle' : '--color-bg-elevated';
    if (accent) return '--color-primary';
    return Math.max(...rgb) < 190 ? '--color-text-secondary' : '--color-text-primary';
  }
  function adapt(value, property, resolved = false) {
    if (value.includes('var(') || value.includes('url(')) return value;
    return value.replace(literal, color => {
      const token = tokenFor(color, property);
      return token ? (resolved ? theme.tokens[token] || color : `var(${token})`) : color;
    });
  }
  function adaptDeclaration(style) {
    for (const property of Array.from(style)) {
      if (!/color|background|border|outline|shadow|fill|stroke|^--/i.test(property)) continue;
      const value = style.getPropertyValue(property);
      const next = adapt(value, property);
      if (next !== value) style.setProperty(property, next, style.getPropertyPriority(property));
    }
  }
  function adaptRules(rules) {
    for (const rule of rules) {
      if (rule.style) adaptDeclaration(rule.style);
      if (rule.cssRules) adaptRules(rule.cssRules);
    }
  }
  function adaptElement(element) {
    if (element.style) adaptDeclaration(element.style);
    for (const attr of ['fill', 'stroke']) {
      const value = element.getAttribute?.(attr);
      if (value) {
        const next = adapt(value, attr);
        if (next !== value) element.setAttribute(attr, next);
      }
    }
  }
  function adaptTree(element) {
    adaptElement(element);
    element.querySelectorAll?.('*').forEach(adaptElement);
  }
  // Canvas pixels do not inherit CSS. Map UI drawing colors at draw time so
  // redraws and interactive chart updates follow the current theme as well.
  const prototype = window.CanvasRenderingContext2D?.prototype;
  if (prototype) {
    for (const method of ['fillRect', 'fillText', 'stroke', 'strokeRect', 'strokeText']) {
      const original = prototype[method];
      prototype[method] = function (...args) {
        const key = method.startsWith('stroke') ? 'strokeStyle' : 'fillStyle';
        const previous = this[key];
        if (typeof previous === 'string') this[key] = adapt(previous, method, true);
        try { return original.apply(this, args); }
        finally { this[key] = previous; }
      };
    }
  }
  const root = document.getElementById('root');
  const observer = new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes') adaptElement(record.target);
      else record.addedNodes.forEach(node => { if (node.nodeType === 1) adaptTree(node); });
    }
  });
  observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'fill', 'stroke'] });
  return nextTheme => {
    theme = nextTheme;
    document.documentElement.dataset.theme = theme.mode;
    document.documentElement.style.colorScheme = theme.mode;
    for (const sheet of document.styleSheets) adaptRules(sheet.cssRules);
    for (const [key, value] of Object.entries(theme.tokens)) {
      document.documentElement.style.setProperty(key, value, 'important');
    }
  };
}
